import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { fullDate } from '../lib/format';
import { step } from '../telemetry';
import type { DnsRecord, Domain, DomainReport } from '../types';
import { Check, DnsFlags, DnsGroups } from './DnsRecords';

/*
  AYARLAR → DNS QEYDLƏRİ.

  ⚠️ NİYƏ ƏLAVƏ OLUNDU (2026-08-17) — REAL MÜŞTƏRİ İTKİSİ.
  Yazılar YALNIZ qeydiyyatdan sonrakı qarşılama ekranında görünürdü.
  O ekran isə domenin vəziyyətindən asılı olaraq mərhələ dəyişir: domen
  aktivləşən kimi DNS mərhələsi ARADAN QALXIR. Yəni müştəri MX-i qurub
  poçtu işə salandan sonra SPF/DKIM yazılarını bir daha heç yerdə görə
  bilmirdi — halbuki onlar məhz «sonra da əlavə edə bilərsiniz» deyə
  təqdim olunur. Nəticədə söz verdiyimiz addımı yerinə yetirməyin yolu
  qalmırdı və sual dəstəyə düşürdü.

  Ayarlar bunun düzgün yeridir: müştəri poçt parametrlərini axtaranda
  onsuz da bura baxır və DNS də eyni «quraşdırma» ehtiyacının bir
  hissəsidir.

  ⚠️ YALNIZ ŞİRKƏT ADMİNİ ÜÇÜN. Serverdə `/api/domains/:id/dns` ucu
  `requireAdmin` ilə qorunur. Adi istifadəçiyə göstərsək, bölmə açılan
  kimi 403 alıb boş qalar — nə məlumat verər, nə də səbəbini izah edər.
  Ona görə çağıran tərəf `user.isTenantAdmin` yoxlayır.
*/

export function DnsSettings(): React.ReactElement | null {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [pick, setPick] = useState<string | null>(null);
  const [records, setRecords] = useState<DnsRecord[] | null>(null);
  const [report, setReport] = useState<DomainReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareTo, setShareTo] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .domains()
      .then(({ domains: list }) => {
        if (cancelled) return;
        setDomains(list);
        setPick(list[0]?.id ?? null);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /*
    ⚠️ DOMEN DƏYİŞƏNDƏ YAZILAR DA, HESABAT DA SIFIRLANIR.
    Hesabat bir domenə aiddir. Köhnəsini ekranda saxlasaq, müştəri
    B domeninin altında A domeninin «✓ MX» nəticəsini görər və
    qurulmamış domeni qurulmuş sanar.
  */
  useEffect(() => {
    if (!pick) return;
    let cancelled = false;
    setRecords(null);
    setReport(null);
    setSentTo(null);
    void api
      .domainDns(pick)
      .then(({ records: r }) => {
        if (!cancelled) setRecords(r);
      })
      .catch(() => {
        if (!cancelled) setError('Yazılar yüklənmədi. Səhifəni yeniləyin.');
      });
    return () => {
      cancelled = true;
    };
  }, [pick]);

  const current = domains.find((d) => d.id === pick) ?? null;

  const runVerify = useCallback(async (): Promise<void> => {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      step(`DNS yoxlanılır (Ayarlar): ${current.domain}`);
      const { report: rep, activated } = await api.verifyDomain(current.id);
      setReport(rep);
      // Bayraqlar və «son yoxlama» vaxtı dərhal təzələnir — əks halda
      // düymə basılır, nəticə gəlir, amma yuxarıdakı zolaq köhnə qalır.
      setDomains((prev) =>
        prev.map((d) =>
          d.id === current.id
            ? {
                ...d,
                active: d.active || activated,
                verified: d.verified || activated,
                mxOk: rep.mx.ok,
                spfOk: rep.spf.ok,
                dkimOk: rep.dkim.ok,
                dmarcOk: rep.dmarc.ok,
                checkedAt: new Date().toISOString(),
              }
            : d,
        ),
      );
    } catch {
      setError('Yoxlama alınmadı. Bir azdan yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }, [current]);

  // Domen yoxdursa bölmə ÜMUMİYYƏTLƏ göstərilmir — boş kart sual doğurur.
  if (loading || !current) return null;

  return (
    <section className="admin-card">
      <h2>DNS qeydləri</h2>
      <p className="onboard-note">
        Domeninizin idarə panelində (registrar və ya Cloudflare) olmalı olan
        yazılar. Poçt işləyirsə bunlar artıq qurulub — burada saxlanılır ki,
        lazım olanda yenidən baxa və ya DNS-i quran şəxsə göndərə biləsiniz.
      </p>

      {/*
        ⚠️ SEÇİCİ YALNIZ BİRDƏN ÇOX DOMEN VARSA. Tək domendə düymə
        sırası «nəyisə seçməliyəm?» sualı yaradır və heç nə vermir.
      */}
      {domains.length > 1 && (
        <div className="dns-picker" role="group" aria-label="Domen seçimi">
          {domains.map((d) => (
            <button
              key={d.id}
              type="button"
              className={d.id === pick ? 'dns-pick is-on' : 'dns-pick'}
              aria-pressed={d.id === pick}
              onClick={() => setPick(d.id)}
            >
              {d.domain}
            </button>
          ))}
        </div>
      )}

      <div className="dns-state">
        <strong>{current.domain}</strong>
        <span className={current.active ? 'state is-ok' : 'state is-wait'}>
          {current.active ? 'Aktiv' : 'Gözləyir'}
        </span>
        <DnsFlags d={current} />
        {current.checkedAt && (
          <span className="dns-checked">Son yoxlama: {fullDate(current.checkedAt)}</span>
        )}
      </div>

      {error && <p className="field-hint is-warn">{error}</p>}

      {records === null ? (
        <p className="onboard-note">Yüklənir…</p>
      ) : (
        <DnsGroups records={records} />
      )}

      {report ? (
        <ul className="chk-list">
          <Check label="Sahiblik" r={report.ownership} />
          <Check label="MX" r={report.mx} />
          <Check label="SPF" r={report.spf} />
          <Check label="DKIM" r={report.dkim} />
          <Check label="DMARC" r={report.dmarc} />
        </ul>
      ) : null}

      <div className="dns-verify">
        <button className="btn-secondary" onClick={() => void runVerify()} disabled={busy}>
          {busy ? 'Yoxlanılır…' : 'Yenidən yoxla'}
        </button>
      </div>

      {/*
        DNS-İ QURAN BAŞQA ŞƏXSDİRSƏ.
        ⚠️ Qarşılama ekranındakı ilə EYNİ ucdur. Müştərinin registrar
        parolunu tutan adamı çox vaxt özü deyil; ilk gün göndərməyi
        unudubsa, yolu burada da olmalıdır.
      */}
      <div className="dns-share">
        {sentTo ? (
          <p className="onboard-note is-ok">
            Yazılar <strong>{sentTo}</strong> ünvanına göndərildi.
          </p>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!current || !shareTo.trim()) return;
              setBusy(true);
              setError(null);
              void api
                .emailDnsRecords(current.id, shareTo.trim())
                .then((r) => setSentTo(r.sentTo))
                .catch((err: unknown) =>
                  setError(
                    err instanceof ApiError && err.code === 'too_many_attempts'
                      ? 'Çox cəhd edildi — bir saatdan sonra yenidən yoxlayın.'
                      : 'Məktub göndərilmədi. Ünvanı yoxlayın.',
                  ),
                )
                .finally(() => setBusy(false));
            }}
          >
            <label className="field">
              <span>DNS-i quran başqa şəxsdirsə, yazıları ona göndərin</span>
              <div className="dns-share-row">
                <input
                  type="email"
                  placeholder="it@sirketiniz.az"
                  value={shareTo}
                  onChange={(e) => setShareTo(e.target.value)}
                />
                <button className="btn-secondary" type="submit" disabled={busy || !shareTo.trim()}>
                  Göndər
                </button>
              </div>
            </label>
          </form>
        )}
      </div>
    </section>
  );
}
